import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnChanges,
  OnInit,
} from '@angular/core';
import { SideBarItem } from './sideMenuItem';
import { AuthRepository, getStoredToken } from 'app/state/auth.repository';
import { MessageText, ToastService } from 'app/helpers/services/toastService';
import { EnvState } from 'app/modules/shared/helpers/env-state';
import { UiRepository } from 'app/state/ui.repository';
import {
  GeneralAppDataDto,
  TenantMenuItemData,
} from 'app/api/models/auth/sideBarInfoDto';
import { AuthService } from 'app/state/auth.service';
import { GeneralDataRepository } from '../../../../../helpers/repository/general-data.repository';
import { DocumentTypesRepository } from 'app/state/documenttype.repository';
import { DatalistOption } from 'app/state/abstract/base.repository';
import { NavigationEnd, Router } from '@angular/router';
import { LocalStorageService } from 'app/api/services/local-storage-service';
import { NotificationsService } from 'app/state/notifications.service';
import { catchError, combineLatest, filter, forkJoin, of, take } from 'rxjs';
import { NotificationsRepository } from 'app/state/notifications.repository';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DocumentTypesService } from 'app/state/documenttype.service';
import { UiEventsService } from 'app/api/services/signal.service';
import { SidebarState } from 'app/api/services/sidebarState.service';
import { TenantFeatures } from 'app/state/feature.repository';
import { SalesProfileService } from 'app/api/services/sales-profile.service';

@UntilDestroy()
@Component({
    selector: 'app-sidebar-items',
    templateUrl: './sidebarItems.component.html',
    styleUrls: ['./sidebarItems.component.scss'],
    standalone: false
})
export class SidebarItemsComponent implements OnInit {
  allCRMSideMenuItems = [
    { routerLink: '/CRM/dashboard' },
    { routerLink: '/CRM/deals' },
    { routerLink: '/CRM/accounts' },
    { routerLink: '/CRM/contacts' },
    { routerLink: '/CRM/tasks' },
  ];

  allCRMAdminSideMenuItems = [
    { routerLink: '/CRM/taskTypes' },
    { routerLink: '/CRM/tags' },
    { routerLink: '/CRM/lists' },
  ];

  allMainSideMenuItems: Array<SideBarItem> = [];
  allIgniteSideMenuItems: Array<SideBarItem> = [];
  allAdminSideMenuItems: Array<SideBarItem> = [];
  allLobbySideMenuItems: Array<SideBarItem> = [];
  allReceptionSideMenuItems: Array<SideBarItem> = [];

  sidebarData: GeneralAppDataDto = new GeneralAppDataDto();
  tenantsOptions: Array<DatalistOption> = [];
  featuresData: Array<TenantMenuItemData> = [];
  tenantId: string | null = null;
  notificationsCount: any;
  collapse: boolean = false;
  isFromCRM?: boolean = false;
  aiAssistantEnabled: boolean = false;
  isAnyAdmin: boolean = false;
  isSuperAdmin: boolean = false;
  gamificationEnabled: boolean = false;

  /** True when the tenant has the sales-profile questionnaire enabled (same
   *  gate the home page uses: Questionnaire + PersonalPlan). */
  hasQuestionnaireFeature: boolean = false;
  /** True once the current user has completed the questionnaire. */
  // null = not yet known (don't lock before the first /me resolves).
  questionnaireCompleted: boolean | null = null;

  constructor(
    public env: EnvState,
    public ui: UiRepository,
    private auth: AuthService,
    public repo: AuthRepository,
    public generalDataRepo: GeneralDataRepository,
    public doctypesRepo: DocumentTypesRepository,
    public router: Router,
    private storage: LocalStorageService,
    private notificationsService: NotificationsRepository,
    private zone: NgZone,
    public doctypesService: DocumentTypesService,
    private uiEvents: UiEventsService,
    public sidebarState: SidebarState,
    private toastService: ToastService,
    private salesProfileService: SalesProfileService,
  ) {}

  ngOnInit(): void {
    this.loadQuestionnaireStatus();

    // React in real time to finishing / resetting the questionnaire (the
    // homepage pushes the new state into completed$), not just on load.
    this.salesProfileService.completed$
      .pipe(untilDestroyed(this))
      .subscribe((completed) => {
        this.questionnaireCompleted = completed;
      });

    combineLatest([
      this.generalDataRepo.allGeneralData$,
      this.generalDataRepo.allMainSideMenuItems$,
      this.generalDataRepo.allIgniteSideMenuItems$,
      this.generalDataRepo.allAdminSideMenuItems$,
      this.notificationsService.notificationCount$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          generalData,
          mainItems,
          igniteItems,
          adminItems,
          notificationCount,
        ]) => {
          this.featuresData = generalData.tenantMenuItemsData;
          this.aiAssistantEnabled =
            generalData.featuresForUser?.includes(TenantFeatures.AIAssistant) ??
            false;
          this.gamificationEnabled =
            generalData.featuresForUser?.includes(TenantFeatures.Gamification) ??
            false;
          const features = generalData.features ?? [];
          this.hasQuestionnaireFeature =
            features.includes(TenantFeatures.Questionnaire) &&
            features.includes(TenantFeatures.PersonalPlan);
          if (notificationCount) {
            this.notificationsCount = notificationCount;

            window.dispatchEvent(new Event('resize'));
          }

          const updateItems = (items: any[]) => {
            items.forEach((item) => {
              this.getItemName(item);
            });
          };

          const notification = mainItems.filter(
            (i) => i.title === 'notifications',
          );
          const others = mainItems.filter((i) => i.title !== 'notifications');
          [...others, ...notification].forEach((item) => {
            const match = notificationCount.find(
              (n: any) => n.groupName == item.title,
            );
            item.notificationsCount = match ? match.count : 0;
          });
          this.allMainSideMenuItems = [...others, ...notification];
          this.allIgniteSideMenuItems = igniteItems;
          this.allAdminSideMenuItems = adminItems;

          this.crm =
            this.allMainSideMenuItems.filter((x) =>
              x.title.toLocaleLowerCase().includes('crm'),
            ).length > 0;
          this.collapse = this.crm || generalData.activeUserData.isAnyAdmin;
          this.isAnyAdmin = generalData.activeUserData.isAnyAdmin;
          this.isSuperAdmin = generalData.activeUserData.isSuperAdmin;
          this.allMainSideMenuItems = this.allMainSideMenuItems.filter(
            (x) => !x.title.toLocaleLowerCase().includes('crm'),
          );

          this.isFromCRM = generalData.customTenantProperties.isFromCRM;
          if (this.isFromCRM) {
            const order = [
              '/product',
              '/kemihotelletapv',
              '/data-room/6e5a4bec-d4ea-416a-751f-08de9628d6a9', // Etiketter
              '/data-room/94b04551-f52c-4f78-7520-08de9628d6a9', // Sikkerhedsvurdering
              '/data-room/73081d9b-63d3-472a-7521-08de9628d6a9', // Diverse dokumentation
              '/data-room/270d3cce-db2e-4448-d607-08de9573475a', // Lovservice
            ];

            this.allLobbySideMenuItems = this.allMainSideMenuItems
              .filter(
                (f) =>
                  f.routerLink.includes('/product') ||
                  f.routerLink.includes('/kemihotelletapv') ||
                  f.routerLink.includes('data-room'),
              )
              .sort((a, b) => {
                const aIndex = order.findIndex((o) => a.routerLink.includes(o));
                const bIndex = order.findIndex((o) => b.routerLink.includes(o));

                const aOrder = aIndex === -1 ? Infinity : aIndex;
                const bOrder = bIndex === -1 ? Infinity : bIndex;

                return aOrder - bOrder;
              });

            this.allReceptionSideMenuItems = this.allMainSideMenuItems.filter(
              (f) => f.routerLink.includes('/toolbox'),
            );
            this.allReceptionSideMenuItems.push({
              displayedName: 'news',
              notificationsCount: 0,
              routerLink: '/posts',
              svgPath: 'assets/icons/sidebar/news.svg',
              title: 'news',
            });
            this.allMainSideMenuItems = this.allMainSideMenuItems.filter(
              (item) =>
                !this.allLobbySideMenuItems.includes(item) &&
                !this.allReceptionSideMenuItems.includes(item) &&
                item.routerLink != '/posts',
            );

            updateItems(this.allLobbySideMenuItems);
            updateItems(this.allReceptionSideMenuItems);
          }

          this.allMainSideMenuItems = this.allMainSideMenuItems.filter(
            (f) => !f.routerLink.includes('/notifications'),
          );
          updateItems(this.allMainSideMenuItems);
          updateItems(this.allIgniteSideMenuItems);
          updateItems(this.allAdminSideMenuItems);

          // Now that the section item lists are populated, expand the section
          // that contains the current route (handles a fresh load / deep link
          // where the NavigationEnd below fired before the data arrived).
          this.autoExpandActiveSection();
        },
      );

    // Keep the section containing the active route expanded as the user
    // navigates (the open flags otherwise only change on manual header clicks).
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => this.autoExpandActiveSection());

    combineLatest([
      this.generalDataRepo.tenantsOptions$,
      this.storage.getTenantId$(),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([items, id]) => {
        this.tenantsOptions = [...items].sort((a, b) =>
          a.label.localeCompare(b.label),
        );

        // Re-check questionnaire completion when the active tenant changes so
        // the sidebar lock reflects the newly-scoped tenant/user.
        if (id !== this.tenantId) {
          this.loadQuestionnaireStatus();
        }
        this.tenantId = id;
      });
  }

  /**
   * Fetch whether the current user has completed the sales-profile
   * questionnaire for the active tenant. Failures degrade to "not completed"
   * so the lock never wrongly stays engaged on a transient error — the lock
   * still only applies when the tenant actually has the questionnaire feature.
   */
  private loadQuestionnaireStatus(): void {
    // getMine() pushes the completion state into the shared completed$ stream,
    // which the ngOnInit subscription reads — so this just triggers the fetch.
    this.salesProfileService
      .getMine()
      .pipe(
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  crm = false;

  menuclose(item?: SideBarItem) {
    this.ui.toggleMobileDrawer(false);

    if (item?.displayedName == 'dataroom') {
      this.uiEvents.emitAction();
    }
  }

  setActiveTenant(tenantId?: any) {
    this.sidebarState.isMainOpen = false;
    this.sidebarState.isCrmOpen = false;
    this.sidebarState.isIgniteOpen = false;
    this.sidebarState.isAdminOpen = false;
    this.sidebarState.isCrmAdminOpen = false;
    const token = getStoredToken();
    if (!token) {
      console.error('Cannot switch tenant: no auth token available');
      return;
    }
    // Switch server-side first; only update client state once the server
    // confirms, so a failed request can't desync the UI onto a tenant the
    // backend isn't scoped to (which shows the previous tenant's data).
    this.auth
      .setActiveTenant(token, tenantId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.tenantId = tenantId;
          if (tenantId) {
            localStorage.setItem('activeTenantId', tenantId);
          } else {
            localStorage.removeItem('activeTenantId');
          }
          this.storage.setTenantId(tenantId);
          this.generalDataRepo.updateAllGeneralData(true);
          this.router.navigate(['/home']);
        },
        error: (err) => {
          console.error('Failed to switch tenant', err);
          this.toastService.showError(MessageText.switchTenantFailed);
        },
      });
  }

  getItemName(inputItem: SideBarItem) {
    inputItem.displayedName = inputItem.title;
    if (inputItem) {
      const featureData = this.featuresData.find(
        (data) =>
          data.name.toLocaleLowerCase() === inputItem.title.toLocaleLowerCase(),
      );

      if (featureData) {
        inputItem.displayedName =
          featureData.customTitle || featureData.name.toLocaleLowerCase();
      }
    }
  }
  test(item: any): boolean {
    const path = this.router.url.split('?')[0].split('#')[0];
    if (item.routerLink === '/toolbox/areas') {
      return path.startsWith('/toolbox') && !path.startsWith('/toolbox/admin');
    }
    if (item.routerLink === '/playbookpilot/pilot/admin') {
      return (
        path.startsWith('/playbook/pilot/admin') ||
        path.startsWith('/playbookpilot/pilot/admin')
      );
    }
    return path === item.routerLink || path.startsWith(item.routerLink + '/');
  }

  isSectionActive(items: any[]): boolean {
    return items.some((item) => this.test(item));
  }

  /**
   * Expand whichever collapsible section contains the current route, so the
   * active item is visible (and highlighted) instead of hidden inside a
   * collapsed group. Only ever opens a section — never force-closes others,
   * so the user's manual toggles on unrelated sections are preserved.
   */
  private autoExpandActiveSection(): void {
    if (
      this.isSectionActive(this.allMainSideMenuItems) &&
      !this.router.url.includes('admin')
    ) {
      this.sidebarState.isMainOpen = true;
    }
    if (this.isSectionActive(this.allCRMSideMenuItems)) {
      this.sidebarState.isCrmOpen = true;
    }
    if (this.isSectionActive(this.allIgniteSideMenuItems)) {
      this.sidebarState.isIgniteOpen = true;
    }
    if (this.isSectionActive(this.allLobbySideMenuItems)) {
      this.sidebarState.isLobbyOpen = true;
    }
    if (this.isSectionActive(this.allReceptionSideMenuItems)) {
      this.sidebarState.isReception = true;
    }
    if (this.isSectionActive(this.allAdminSideMenuItems)) {
      this.sidebarState.isAdminOpen = true;
    }
    if (this.isSectionActive(this.allCRMAdminSideMenuItems)) {
      this.sidebarState.isCrmAdminOpen = true;
    }
    if (
      this.router.url.includes('/ai-assistant') ||
      this.router.url.includes('/ai-advanced-pilot')
    ) {
      this.sidebarState.isAiAssistantOpen = true;
    }
  }
  /**
   * Toggle a boolean flag on SidebarState. Restricted to the open/closed
   * keys — `keyof SidebarState` now also includes the string `searchQuery`,
   * which would make the assignment below a type error.
   */
  toggleBar(
    section:
      | 'isMainOpen'
      | 'isCrmOpen'
      | 'isIgniteOpen'
      | 'isAdminOpen'
      | 'isCrmAdminOpen'
      | 'isAiAssistantOpen'
      | 'isLobbyOpen'
      | 'isReception',
  ) {
    this.sidebarState[section] = !this.sidebarState[section];
  }

  /// Click handler for the parent "CRM" sidebar entry. Always toggles
  /// the sub-menu; only navigates to the dashboard when the click is
  /// the one that *opens* the section — collapsing stays put.
  onCrmHeaderClick() {
    const wasOpen = this.sidebarState.isCrmOpen;
    this.toggleBar('isCrmOpen');
    if (!wasOpen) {
      this.router.navigate(['/CRM/dashboard']);
    }
  }

  /**
   * Live-filter helper. Matches an item's displayed name (or backend title
   * fallback) against the search query in SidebarState. Case-insensitive,
   * substring match. Used by the `filtered*` getters below.
   */
  private matchesSearch(item: SideBarItem): boolean {
    const q = this.sidebarState.searchQuery?.trim().toLowerCase();
    if (!q) return true;
    const label = (item.displayedName || item.title || '').toLowerCase();
    return label.includes(q);
  }

  get filteredMainItems(): SideBarItem[] {
    return this.allMainSideMenuItems.filter((i) => this.matchesSearch(i));
  }
  get filteredIgniteItems(): SideBarItem[] {
    return this.allIgniteSideMenuItems.filter((i) => this.matchesSearch(i));
  }
  get filteredAdminItems(): SideBarItem[] {
    return this.allAdminSideMenuItems.filter((i) => this.matchesSearch(i));
  }
  get filteredLobbyItems(): SideBarItem[] {
    return this.allLobbySideMenuItems.filter((i) => this.matchesSearch(i));
  }
  get filteredReceptionItems(): SideBarItem[] {
    return this.allReceptionSideMenuItems.filter((i) => this.matchesSearch(i));
  }
  /** True when a search is active — the template uses this to auto-expand
   * every section that has at least one matching item, regardless of the
   * user's manual open/closed state. */
  get hasSearch(): boolean {
    return !!this.sidebarState.searchQuery?.trim();
  }

  /**
   * String-based search helper for hardcoded labels (CRM, CRM Admin, AI
   * Advanced Pilot). Returns true when the search query is empty or when
   * the provided label contains it (case-insensitive).
   */
  matchesText(text: string): boolean {
    const q = this.sidebarState.searchQuery?.trim().toLowerCase();
    if (!q) return true;
    return text.toLowerCase().includes(q);
  }

  /** Whether the CRM section has any matching items under the current query. */
  get hasCrmMatches(): boolean {
    return ['Dashboard', 'Deals', 'Accounts', 'Contacts', 'Tasks'].some((t) =>
      this.matchesText(t),
    );
  }

  /** Whether the CRM Admin section has any matching items under the current query. */
  get hasCrmAdminMatches(): boolean {
    return ['Task Types', 'Tags', 'Lists', 'Custom Fields', 'Score Rules'].some(
      (t) => this.matchesText(t),
    );
  }

  /**
   * Class string for a nav-row link (an item under a section).
   * Active = brand-tinted background + brand-tinted text (matches the
   * IGNTR template's red/orange highlight). Inactive = ghost hover.
   */
  rowClass(active: boolean): string {
    const base =
      'flex w-full items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] no-underline transition-colors';
    return active
      ? `${base} row-active font-medium`
      : `${base} text-white/55 hover:bg-white/5 hover:text-white/90`;
  }

  /**
   * Class string for a section header (Main, CRM, Ignite, etc.) — the row
   * that toggles a collapsible group. Active reflects "one of my children
   * is the current route" so the header glows even when collapsed.
   */
  sectionHeaderClass(active: boolean): string {
    const base =
      'mt-1 w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] no-underline transition-colors';
    return active
      ? `${base} text-white`
      : `${base} text-white/60 hover:bg-white/5 hover:text-white/90`;
  }

  /**
   * The sidebar is locked when the tenant has the questionnaire feature and the
   * questionnaire has not been completed. This applies to every role — tenant
   * admins included — so the only way past it is to answer the questions.
   * Superadmins are locked too, but keep the Admin Panel (see
   * adminSectionLocked) so they can still administer the platform.
   */
  sidebarLocked(): boolean {
    return this.hasQuestionnaireFeature && this.questionnaireCompleted === false;
  }

  /**
   * The Admin Panel section is the one exemption from the lock, and only for
   * superadmins — locking them out of it would leave nobody able to administer
   * the platform while the questionnaire is outstanding.
   */
  adminSectionLocked(): boolean {
    return this.sidebarLocked() && !this.isSuperAdmin;
  }

  /** Per-item variant of adminSectionLocked for the Admin Panel entries. */
  isAdminItemLocked(item: {
    routerLink?: string;
    title?: string;
    displayedName?: string;
  }): boolean {
    return this.adminSectionLocked() && !this.isHome(item);
  }

  /**
   * An item is the Home entry when its routerLink is `/home`, or its
   * displayed name / backend title is `home` (case-insensitive).
   */
  isHome(item: { routerLink?: string; title?: string; displayedName?: string }): boolean {
    const link = (item?.routerLink || '').toLowerCase();
    if (link === '/home' || link.startsWith('/home')) return true;
    const label = (item?.displayedName || item?.title || '').toLowerCase();
    return label === 'home';
  }

  /** True when the sidebar is locked and this item is not Home. */
  isItemLocked(item: {
    routerLink?: string;
    title?: string;
    displayedName?: string;
  }): boolean {
    return this.sidebarLocked() && !this.isHome(item);
  }

  /** Danish tooltip shown on locked entries. */
  readonly lockedTooltip = 'Besvar spørgsmålene for at fortsætte.';
}
